/**
 * sRGB ↔ OKLCH conversion. Zero dependencies.
 *
 * WHY OKLCH AND NOT sRGB. A tenant's brand colour has to be turned into a small
 * family of related colours — a button ground, its hover, a wash behind a
 * selected row, a border. Doing that by mixing toward white or black in sRGB
 * (which is what `color-mix(in srgb, …)` does, and what the 34 existing
 * call sites in styles/admin do) shifts the HUE as it goes: a saturated blue
 * mixed toward white drifts purple. OKLCH separates lightness from hue, so
 * moving lightness keeps the customer's colour recognisably theirs.
 *
 * WHY IN TYPESCRIPT AND NOT IN CSS. `oklch(from var(--x) …)` exists and would be
 * elegant, but it is Safari 16.4+ / Chrome 119+ — a cleaner opens the crew
 * portal on whatever phone they own. More decisively: guaranteeing a contrast
 * ratio requires a SEARCH ("lower lightness until the ratio holds"), and CSS has
 * no loop. A value computed here is also a value a test can assert over
 * thousands of inputs. A CSS expression is not.
 *
 * Matrices are Björn Ottosson's Oklab definition (2020).
 */

export interface Rgb {
  /** 0–1, gamma-encoded sRGB. */
  r: number;
  g: number;
  b: number;
}

export interface Oklch {
  /** Perceptual lightness, 0 (black) – 1 (white). */
  l: number;
  /** Chroma, 0 (grey) – ~0.4 in practice. Unbounded in principle. */
  c: number;
  /** Hue angle in degrees, 0–360. Meaningless when chroma is ~0. */
  h: number;
}

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

/** True for exactly the shape we are willing to put into a stylesheet. */
export function isHex6(value: unknown): value is string {
  return typeof value === 'string' && HEX6.test(value);
}

export function hexToRgb(hex: string): Rgb {
  if (!isHex6(hex)) {
    throw new Error(`hexToRgb: expected #rrggbb, got ${JSON.stringify(hex)}`);
  }
  return {
    r: parseInt(hex.slice(1, 3), 16) / 255,
    g: parseInt(hex.slice(3, 5), 16) / 255,
    b: parseInt(hex.slice(5, 7), 16) / 255,
  };
}

function channelToHex(v: number): string {
  const byte = Math.round(clamp01(v) * 255);
  return byte.toString(16).padStart(2, '0');
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Gamma-encoded sRGB → linear-light sRGB.
 *
 * Note the threshold here (0.04045) is the ENCODED-side breakpoint; the inverse
 * below uses the linear-side one (0.0031308). They are not the same number and
 * swapping them is a silent ~1% error near black.
 */
function toLinear(v: number): number {
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function toGamma(v: number): number {
  return v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

/** Linear-light sRGB, 0–1 per channel. Shared with the WCAG luminance maths. */
export function linearRgb(hex: string): Rgb {
  const { r, g, b } = hexToRgb(hex);
  return { r: toLinear(r), g: toLinear(g), b: toLinear(b) };
}

// --- Oklab core -------------------------------------------------------------

function linearRgbToOklab({ r, g, b }: Rgb): { l: number; a: number; bb: number } {
  const lms0 = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const lms1 = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const lms2 = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(lms0);
  const m_ = Math.cbrt(lms1);
  const s_ = Math.cbrt(lms2);

  return {
    l: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    bb: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

function oklabToLinearRgb(l: number, a: number, bb: number): Rgb {
  const l_ = l + 0.3963377774 * a + 0.2158037573 * bb;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * bb;
  const s_ = l - 0.0894841775 * a - 1.291485548 * bb;

  const lms0 = l_ * l_ * l_;
  const lms1 = m_ * m_ * m_;
  const lms2 = s_ * s_ * s_;

  return {
    r: 4.0767416621 * lms0 - 3.3077115913 * lms1 + 0.2309699292 * lms2,
    g: -1.2684380046 * lms0 + 2.6097574011 * lms1 - 0.3413193965 * lms2,
    b: -0.0041960863 * lms0 - 0.7034186147 * lms1 + 1.707614701 * lms2,
  };
}

// --- Public conversions -----------------------------------------------------

export function hexToOklch(hex: string): Oklch {
  const { r, g, b } = hexToRgb(hex);
  const { l, a, bb } = linearRgbToOklab({ r: toLinear(r), g: toLinear(g), b: toLinear(b) });
  const c = Math.sqrt(a * a + bb * bb);
  /*
   * Hue is undefined for a grey. Reporting 0 rather than NaN keeps every
   * downstream comparison total — a greyscale brand degenerates to a neutral
   * ramp that still satisfies every contrast assertion, which is what we want
   * for the tenant who types #FFFFFF.
   */
  const h = c < 1e-6 ? 0 : ((Math.atan2(bb, a) * 180) / Math.PI + 360) % 360;
  return { l, c, h };
}

/** True when this L/C/h lands inside the sRGB cube without clipping. */
function inGamut(l: number, c: number, h: number): boolean {
  const rad = (h * Math.PI) / 180;
  const { r, g, b } = oklabToLinearRgb(l, c * Math.cos(rad), c * Math.sin(rad));
  const EPS = 1e-4;
  return r >= -EPS && r <= 1 + EPS && g >= -EPS && g <= 1 + EPS && b >= -EPS && b <= 1 + EPS;
}

/**
 * Reduce chroma until the colour fits in sRGB, keeping L and hue exactly.
 *
 * Preserving hue is the whole point: clamping the RGB channels instead (the
 * obvious shortcut) moves the hue, so a customer's colour comes back as a
 * different colour at the extremes rather than a less saturated one.
 */
export function gamutClip(l: number, c: number, h: number): Oklch {
  if (c <= 0 || inGamut(l, c, h)) return { l, c: Math.max(0, c), h };
  let lo = 0;
  let hi = c;
  /*
   * 12 halvings resolve chroma to ~1e-4, two orders finer than the 8-bit channel
   * this is about to be rounded into. More iterations buy precision that cannot
   * survive `rgbToHex` and cost real time: this runs inside another bisection,
   * so the two multiply, and the property test derives 4,096 ramps.
   */
  for (let i = 0; i < 12; i += 1) {
    const mid = (lo + hi) / 2;
    if (inGamut(l, mid, h)) lo = mid;
    else hi = mid;
  }
  return { l, c: lo, h };
}

export function oklchToHex({ l, c, h }: Oklch): string {
  const safeL = clamp01(l);
  const clipped = gamutClip(safeL, Math.max(0, c), h);
  const rad = (clipped.h * Math.PI) / 180;
  const lin = oklabToLinearRgb(safeL, clipped.c * Math.cos(rad), clipped.c * Math.sin(rad));
  return rgbToHex({
    r: toGamma(clamp01(lin.r)),
    g: toGamma(clamp01(lin.g)),
    b: toGamma(clamp01(lin.b)),
  });
}

/** Smallest angular distance between two hues, 0–180. */
export function hueDistance(a: number, b: number): number {
  const d = Math.abs(((a - b) % 360) + 360) % 360;
  return d > 180 ? 360 - d : d;
}
